import { inject, Injectable, type Provider } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  CABINET_ITEM_DETAIL_OPENER,
  type CabinetItemDetailOpenRequest,
  type CabinetItemDetailOpener,
  CABINET_PROFILE_FACADE,
} from '@nebalkon-workspace/cabinet-client';
import {
  ItemService,
  type UpdateStaffItemRequest,
} from '@nebalkon-workspace/storage-api';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { StaffUserItemDetailModalComponent } from './staff-user-item-detail-modal.component';

@Injectable()
export class StaffCabinetItemDetailOpener implements CabinetItemDetailOpener {
  readonly #dialog = inject(Dialog);
  readonly #items = inject(ItemService);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #profile = inject(CABINET_PROFILE_FACADE, { optional: true });

  open(request: CabinetItemDetailOpenRequest): void {
    const ref = this.#dialog.open(StaffUserItemDetailModalComponent, {
      data: {
        item: request.item,
        previewUrl: request.previewUrl,
        updating: false,
        editable: request.editable,
        ownerLastName: this.#profile?.userInfo()?.individual?.last_name,
        onChangeStatus: (nextStatus: string): Promise<void> =>
          this.#changeStatus(request.item.id, nextStatus, request),
        onSaveItem: request.editable
          ? (body: UpdateStaffItemRequest): Promise<void> =>
              this.#saveItem(request.item.id, body, request)
          : undefined,
        onDeleteItem: request.editable
          ? (): Promise<void> => this.#deleteItem(request.item.id, request)
          : undefined,
        onItemRefreshed: () => {
          request.reloadCatalog();
        },
        tagSuggestions: request.tagSuggestions,
      },
      panelClass: 'client-item-detail-modal-panel',
    });

    ref.closed.subscribe((changed) => {
      if (changed) {
        request.reloadCatalog();
      }
    });
  }

  async #changeStatus(
    itemId: string,
    nextStatus: string,
    request: CabinetItemDetailOpenRequest,
  ): Promise<void> {
    await firstValueFrom(
      this.#items.updateItemStatus(itemId, {
        new_status: nextStatus,
        reason: this.#translate.instant('admin.users.items.statusChangeReason'),
      }),
    );
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [this.#translate.instant('admin.users.items.statusChangeSuccess')],
    });
    request.reloadCatalog();
  }

  async #saveItem(
    itemId: string,
    body: UpdateStaffItemRequest,
    request: CabinetItemDetailOpenRequest,
  ): Promise<void> {
    await firstValueFrom(this.#items.updateStaffItem(itemId, body));
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [
        this.#translate.instant('admin.storageRequests.orderItemsEditSuccess'),
      ],
    });
    request.reloadCatalog();
  }

  async #deleteItem(
    itemId: string,
    request: CabinetItemDetailOpenRequest,
  ): Promise<void> {
    await firstValueFrom(this.#items.deleteStaffItem(itemId));
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'success',
      text: [this.#translate.instant('admin.users.items.deleteSuccess')],
    });
    request.reloadCatalog();
  }
}

export function provideStaffCabinetItemDetailOpener(): Provider[] {
  return [
    StaffCabinetItemDetailOpener,
    {
      provide: CABINET_ITEM_DETAIL_OPENER,
      useExisting: StaffCabinetItemDetailOpener,
    },
  ];
}
